import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoggerService } from '../logger/logger.service';
import { UrlService } from '../url/url.service';
import { PaginatedUsersDto, UserDto, UserParamsDto } from '../../models/user/user.dto';
import { catchError, map, Observable, throwError } from 'rxjs';
import { LogsType } from '../../../shared/constants/logs-type.enum';
import { mapToXPagination } from '../../models/common/pagination.model';
import { CreateUserDto } from '../../models/user/create-user.dto';
import { UpdateUserDto } from '../../models/user/update-user.dto';

@Injectable({
  providedIn: 'root',
})
export class UserApiService {
  private readonly className = 'UserApiService';
  private functionName = '';

  constructor(
    private readonly http: HttpClient,
    private readonly logger: LoggerService,
    private readonly urlService: UrlService
  ) {}

  /**
   * Gets the users based on the provided parameters.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.userApiService.getApiUsers(paramsUser);
   *
   * // Use this in Interceptors, Guards and Functions
   * userApiService.getApiUsers(paramsUser);
   * ```
   *
   * @param params - The parameters to filter and paginate the users
   * @returns Observable<PaginatedUsersDto> - The users and the pagination information
   */
  getApiUsers(params: UserParamsDto): Observable<PaginatedUsersDto> {
    this.functionName = 'getApiUsers';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);
    let url = '';

    url = this.urlService.getUsersUrl();

    return this.http.get<UserDto[]>(url, {
        params: {
          ...(params.PageNumber !== undefined && { PageNumber: params.PageNumber.toString(), }),
          ...(params.PageSize !== undefined && { PageSize: params.PageSize.toString(), }),
          ...(params.Email && { Email: params.Email, }),
          ...(params.Role && { Role: params.Role }),
        },
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<UserDto[]>) => {
          const users = response.body ?? [];
          const paginationHeader = response.headers.get('x-pagination');
          const pagination = mapToXPagination(paginationHeader);

          return { users, pagination };
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  /**
   * Retrieves a user by its unique identifier.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.userApiService.getApiUserById(userId);
   *
   * // Use this in Interceptors, Guards and Functions
   * userApiService.getApiUserById(userId);
   * ```
   *
   * @param userId - The unique identifier of the user
   * @returns Observable<UserDto> - The user details
   */
  getApiUserById(userId: string): Observable<UserDto> {
    this.functionName = 'getApiUserById';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    const url = this.urlService.getUserByIdUrl(userId);

    return this.http.get<UserDto>(url).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Creates a new user.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.userApiService.createApiUser(user);
   *
   * // Use this in Interceptors, Guards and Functions
   * userApiService.createApiUser(user);
   * ```
   *
   * @param user - The data for the new user
   * @returns Observable<UserDto> - The created user details
   */
  createApiUser(user: CreateUserDto): Observable<UserDto> {
    this.functionName = 'createApiUser';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    const url = this.urlService.getUsersUrl();

    return this.http.post<UserDto>(url, user).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Updates an existing user by its unique identifier.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.userApiService.updateApiUser(userId, user);
   *
   * // Use this in Interceptors, Guards and Functions
   * userApiService.updateApiUser(userId, user);
   * ```
   *
   * @param userId - The unique identifier of the user
   * @param user - The updated data for the user
   * @returns Observable<void> - Indicates the operation result
   */
  updateApiUser(userId: string, user: UpdateUserDto): Observable<void> {
    this.functionName = 'updateApiUser';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    const url = this.urlService.getUserByIdUrl(userId);
    return this.http.put<void>(url, user).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Deletes a user by its unique identifier.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.userApiService.deleteApiUser(userId);
   *
   * // Use this in Interceptors, Guards and Functions
   * userApiService.deleteApiUser(userId);
   * ```
   *
   * @param userId - The unique identifier of the user
   * @returns Observable<void> - Indicates the operation result
   */
  deleteApiUser(userId: string): Observable<void> {
    this.functionName = 'deleteApiUser';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    const url = this.urlService.getUserByIdUrl(userId);
    return this.http.delete<void>(url).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
}
